import {Dispatch, SetStateAction, useCallback, useState} from 'react'
import {useCallWhenChanged} from '@containers/hooks'
import {AccordionProps} from './accordion'
import {AccordionItemData} from './types'

export type UseAccordionOptions = Pick<
  AccordionProps,
  'items' | 'multi' | 'disabled' | 'defaultOpenedIds'
>

type SetOpenedIds = Dispatch<SetStateAction<string[]>>

const prepareDefaultOpenedIds = (
  items: AccordionItemData[],
  defaultOpenedIds: string[] | undefined,
  disabled?: boolean
): string[] => {
  // open nothing if `disabled` is set to `true`
  if (!defaultOpenedIds || disabled) {
    return []
  }

  // default opened items are those that exist in a list and are not disabled
  const ids: string[] = []
  for (const openedId of defaultOpenedIds) {
    const item = items.find(x => x.id === openedId)
    if (item && !item.disabled) {
      ids.push(item.id)
    }
  }
  return ids
}

/**
 * Clear opened ids only if `disabled` is changed to `true`
 */
const useCollapseAllIfDisabled = (
  disabled: boolean | undefined,
  setOpenedIds: SetOpenedIds
): void => {
  const cb = useCallback(
    (disabled: boolean): void => {
      disabled && setOpenedIds([])
    },
    [setOpenedIds]
  )

  useCallWhenChanged(disabled, cb)
}

/**
 * Collapse disabled items when `items` is changed
 */
const useCollapseDisabledItems = (
  items: AccordionItemData[],
  setOpenedIds: SetOpenedIds
): void => {
  const cb = useCallback(
    (items: AccordionItemData[]) => {
      const disabledIds = new Set(items.filter(x => x.disabled).map(x => x.id))
      setOpenedIds(openedIds => openedIds.filter(id => !disabledIds.has(id)))
    },
    [setOpenedIds]
  )

  useCallWhenChanged(items, cb)
}

interface UseOnItemClickOptions {
  items: AccordionItemData[]
  multi?: boolean
  disabled?: boolean
  setOpenedIds: SetOpenedIds
}

type OnItemClick = (clickedId: string) => void

const useOnItemClick = ({
  items,
  multi,
  disabled,
  setOpenedIds
}: UseOnItemClickOptions): OnItemClick =>
  useCallback(
    clickedId => {
      if (disabled) {
        return
      }

      setOpenedIds(prevIds => {
        const item = items.find(x => x.id === clickedId)
        if (!item || item.disabled) {
          return prevIds
        }

        if (multi) {
          return prevIds.includes(item.id)
            ? prevIds.filter(x => x !== item.id)
            : [...prevIds, item.id]
        } else {
          return prevIds.includes(item.id) ? [] : [item.id]
        }
      })
    },
    [setOpenedIds, items, multi, disabled]
  )

export const useAccordion = ({
  items,
  multi,
  disabled,
  defaultOpenedIds
}: UseAccordionOptions): {
  openedIds: string[]
  onItemClick: OnItemClick
} => {
  const [openedIds, setOpenedIds] = useState<string[]>(() =>
    prepareDefaultOpenedIds(items, defaultOpenedIds, disabled)
  )

  useCollapseAllIfDisabled(disabled, setOpenedIds)
  useCollapseDisabledItems(items, setOpenedIds)

  const onItemClick = useOnItemClick({items, multi, disabled, setOpenedIds})

  return {openedIds, onItemClick}
}
